import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, PaperPlaneRight, BookOpenText, Cpu } from "@phosphor-icons/react";
import {
  fetchAiModels,
  listAiSessions,
  createAiSession,
  getAiSession,
  deleteAiSession,
  streamAiChat,
  saveAiToHistory,
  saveAiCustomTable,
  listKnowledge,
  formatApiError,
  AI_DRAFT_KEY,
} from "../lib/api";
import ChatMessage from "../components/ai/ChatMessage";
import SessionList from "../components/ai/SessionList";
import SaveToHistoryDialog from "../components/ai/SaveToHistoryDialog";
import CustomTableDialog from "../components/ai/CustomTableDialog";

const SUGGESTIONS = [
  "Susun estimasi biaya operasi appendektomi, kelas 2, pasien umum, elektif.",
  "Operasi caesaria gemelli kelas VIP, pasien umum, cito. Sertakan manajemen nyeri sesuai SK.",
  "ORIF fraktur femur (Besar 1 Orthopedi) kelas 1 dengan implant plate & screw dan C-Arm.",
  "Buat rincian biaya paket rawat inap 3 hari kelas 2 pasca operasi (kamar, visite, obat, lab) dalam tabel.",
];

export default function AIModePage() {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const [models, setModels] = useState([]);
  const [modelId, setModelId] = useState("");
  const [penjamin, setPenjamin] = useState("UMUM");
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(null); // {content}
  const [knowledgeCount, setKnowledgeCount] = useState(null);
  const [saveTarget, setSaveTarget] = useState(null);
  const [customTarget, setCustomTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef(null);

  const activeModel = models.find((m) => m.id === modelId);

  useEffect(() => {
    fetchAiModels()
      .then((d) => {
        setModels(d.models);
        setModelId((prev) => prev || d.default);
      })
      .catch((e) => toast.error(formatApiError(e)));
    listKnowledge().then((d) => setKnowledgeCount(d.length)).catch(() => {});
    refreshSessions();
  }, []);

  const refreshSessions = () => listAiSessions().then(setSessions).catch(() => {});

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    getAiSession(sessionId)
      .then((s) => {
        setMessages(s.messages || []);
        if (s.model) setModelId(s.model);
      })
      .catch(() => {
        toast.error("Sesi tidak ditemukan.");
        navigate("/ai");
      });
  }, [sessionId, navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;
    let sid = sessionId;
    try {
      if (!sid) {
        const s = await createAiSession({ model: modelId });
        sid = s.id;
        navigate(`/ai/${sid}`, { replace: true });
      }
    } catch (e) {
      return toast.error(formatApiError(e));
    }
    setInput("");
    const userMsg = { id: `tmp-${Date.now()}`, role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setStreaming({ content: "" });
    try {
      await streamAiChat(
        sid,
        { message: text, model: modelId, penjamin },
        {
          delta: (ev) => setStreaming((s) => ({ content: (s?.content || "") + ev.content })),
          error: (ev) => toast.error("AI gagal menjawab: " + ev.message),
          done: (ev) => {
            setMessages((prev) => [...prev, ev.message]);
            setStreaming(null);
            refreshSessions();
          },
        },
      );
    } catch (e) {
      toast.error(e.message || "Gagal mengirim pesan.");
    } finally {
      setStreaming(null);
    }
  };

  const handleSendToCalculator = (msg) => {
    sessionStorage.setItem(
      AI_DRAFT_KEY,
      JSON.stringify({ proposal: msg.proposal, model_label: msg.model_label, session_id: sessionId }),
    );
    navigate("/?from=ai");
  };

  const handleSaveToHistory = async (form) => {
    setSaving(true);
    try {
      const doc = await saveAiToHistory(sessionId, {
        proposal: saveTarget.proposal,
        model: saveTarget.model,
        ...form,
      });
      toast.success("Tersimpan ke riwayat (sumber: AI).");
      setSaveTarget(null);
      navigate(`/print/${doc.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCustom = async (form) => {
    setSaving(true);
    try {
      const doc = await saveAiCustomTable(sessionId, { ...form, model: customTarget.model });
      toast.success("Tabel custom tersimpan ke riwayat.");
      setCustomTarget(null);
      navigate(`/print/${doc.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSession = async (s) => {
    if (!window.confirm(`Hapus percakapan "${s.title}"?`)) return;
    try {
      await deleteAiSession(s.id);
      refreshSessions();
      if (s.id === sessionId) navigate("/ai");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="ai-mode-page">
      <aside className="lg:col-span-3 flex flex-col gap-4 lg:h-[calc(100vh-130px)]">
        <div className="bg-violet-700 text-white rounded shadow-sm p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-violet-200 flex items-center gap-1.5">
            <Sparkle size={14} weight="fill" /> Mode AI
          </div>
          <div className="text-lg font-bold leading-tight mt-1">Asisten Penyusunan Tarif Operasi</div>
          <div className="mt-3 text-xs text-violet-100 flex items-center gap-1.5">
            <BookOpenText size={14} />
            {knowledgeCount == null ? "…" : `${knowledgeCount} dokumen Knowledge AI aktif`}
          </div>
          <div className="mt-3 pt-3 border-t border-violet-500/60">
            <label className="text-[11px] uppercase tracking-wider font-bold text-violet-200 flex items-center gap-1.5">
              <Cpu size={12} /> Versi AI
            </label>
            <select
              value={modelId}
              onChange={(e) => setModelId(e.target.value)}
              className="mt-1 w-full bg-violet-800 border border-violet-500 text-white rounded px-2 py-1.5 text-sm"
              data-testid="ai-model-select"
            >
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label} · {m.vendor}
                </option>
              ))}
            </select>
            {activeModel && (
              <div className="text-[11px] text-violet-200 mt-1" data-testid="ai-model-version">
                Model: <span className="font-mono text-white">{activeModel.id}</span>
              </div>
            )}
            <label className="mt-3 block text-[11px] uppercase tracking-wider font-bold text-violet-200">
              Konteks Penjamin
            </label>
            <select
              value={penjamin}
              onChange={(e) => setPenjamin(e.target.value)}
              className="mt-1 w-full bg-violet-800 border border-violet-500 text-white rounded px-2 py-1.5 text-sm"
              data-testid="ai-penjamin-select"
            >
              <option value="UMUM">Umum / Asuransi</option>
              <option value="BPJSTK">BPJSTK</option>
            </select>
          </div>
        </div>
        <div className="flex-1 min-h-[240px]">
          <SessionList
            sessions={sessions}
            activeId={sessionId}
            onSelect={(id) => navigate(`/ai/${id}`)}
            onNew={() => navigate("/ai")}
            onDelete={handleDeleteSession}
          />
        </div>
      </aside>

      <section className="lg:col-span-9 bg-[#F8F7FC] border border-slate-200 rounded shadow-sm flex flex-col lg:h-[calc(100vh-130px)]">
        <div className="flex-1 overflow-y-auto p-5 md:p-6 flex flex-col gap-5" data-testid="ai-chat-area">
          {messages.length === 0 && !streaming ? (
            <div className="m-auto max-w-xl text-center" data-testid="ai-empty-state">
              <div className="w-14 h-14 rounded-full bg-violet-600 text-white flex items-center justify-center mx-auto">
                <Sparkle size={28} weight="fill" />
              </div>
              <h2 className="text-lg md:text-xl font-bold text-slate-900 mt-4">
                Jelaskan kasus operasinya, AI menyusun rincian biayanya.
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                AI memakai master tarif rumah sakit dan dokumen Knowledge AI (buku tarif, SK layanan)
                untuk menyusun komponen biaya. Hasilnya bisa dikirim ke Kalkulator atau langsung
                disimpan ke Riwayat.
              </p>
              <div className="mt-5 flex flex-col gap-2 text-left">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="text-left text-sm bg-white border border-slate-200 hover:border-violet-400 hover:bg-violet-50 rounded px-4 py-2.5 transition-colors"
                    data-testid={`ai-suggestion-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((m) => (
                <ChatMessage
                  key={m.id}
                  msg={m}
                  onSendToCalculator={handleSendToCalculator}
                  onSaveToHistory={(msg) => setSaveTarget(msg)}
                  onSaveCustom={(msg) => setCustomTarget(msg)}
                />
              ))}
              {streaming && (
                <ChatMessage
                  msg={{ role: "assistant", content: streaming.content, model_label: activeModel?.label }}
                  streaming
                />
              )}
              <div ref={bottomRef} />
            </>
          )}
        </div>
        <div className="border-t border-slate-200 bg-white p-4">
          <div className="flex gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              rows={2}
              placeholder="Tulis kasus operasi… (Enter untuk kirim, Shift+Enter baris baru)"
              className="flex-1 resize-none bg-white border border-slate-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500"
              data-testid="ai-chat-input"
            />
            <button
              onClick={() => send()}
              disabled={!!streaming || !input.trim()}
              className="bg-violet-600 text-white h-[58px] px-5 rounded hover:bg-violet-700 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="ai-chat-send"
            >
              <PaperPlaneRight size={18} weight="fill" /> {streaming ? "…" : "Kirim"}
            </button>
          </div>
          <div className="text-[11px] text-slate-500 mt-2">
            Rekomendasi AI bersifat bantuan. Tarif resmi tetap dihitung oleh Kalkulator berdasarkan master data.
          </div>
        </div>
      </section>

      {customTarget && (
        <CustomTableDialog
          table={customTarget.custom_table}
          saving={saving}
          onCancel={() => setCustomTarget(null)}
          onSubmit={handleSaveCustom}
        />
      )}
      {saveTarget && (
        <SaveToHistoryDialog
          proposal={saveTarget.proposal}
          saving={saving}
          onCancel={() => setSaveTarget(null)}
          onSubmit={handleSaveToHistory}
        />
      )}
    </div>
  );
}
